import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { api } from '../api';

// Export the accepted roster as a who's-who (Word / PDF / CSV). Mirrors the
// Chronology tab's export menu — same formats, same download path.

const FORMATS = [['docx', 'Word document (.docx)'], ['pdf', 'PDF'], ['csv', 'CSV (spreadsheet)']];

async function downloadWitnessList(caseId, fmt) {
  const resp = await api.raw('GET', `/cases/${caseId}/witnesses/export.${fmt}`);
  if (!resp.ok) throw new Error(`Export failed (HTTP ${resp.status})`);
  const blob = await resp.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = `witness-list.${fmt}`;
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
}

function useWitnessExport(caseId, onDone) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const exportAs = async (fmt) => {
    setBusy(true);
    setError(null);
    try {
      await downloadWitnessList(caseId, fmt);
      if (onDone) onDone();
    } catch (err) {
      setError(err.message || 'Export failed');
    } finally {
      setBusy(false);
    }
  };
  return { busy, error, setError, exportAs };
}

const S = {
  wrap: { position: 'relative', display: 'inline-block' },
  backdrop: { position: 'fixed', inset: 0, zIndex: 19 },
  menu: { position: 'absolute', right: 0, top: '112%', background: '#fff', border: '1px solid #c7ccd6', borderRadius: 8, boxShadow: '0 6px 18px rgba(0,0,0,0.14)', zIndex: 20, minWidth: 184, overflow: 'hidden' },
  menuItem: { display: 'block', width: '100%', textAlign: 'left', padding: '9px 12px', border: 'none', borderBottom: '1px solid #f0f2f6', background: '#fff', cursor: 'pointer', fontSize: 13 },
  note: { padding: '8px 12px', fontSize: 12, color: '#667085', maxWidth: 220 },
  error: { padding: '8px 12px', fontSize: 12, color: '#b91c1c', maxWidth: 220 },
  card: { display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, margin: '8px 0', padding: '8px 12px', border: '1px solid #dee2e6', borderRadius: 8, fontSize: 13 },
  cardActions: { display: 'flex', flexWrap: 'wrap', gap: 6 },
};

/**
 * Props:
 *   caseId
 *   acceptedCount — only accepted witnesses export; 0 disables the formats
 *   disabled
 */
export default function WitnessExportMenu({ caseId, acceptedCount, disabled }) {
  const [open, setOpen] = useState(false);
  const { busy, error, setError, exportAs } = useWitnessExport(caseId, () => setOpen(false));

  return (
    <div style={S.wrap}>
      <button
        type="button"
        className="btn btn-sm"
        onClick={() => { setError(null); setOpen(o => !o); }}
        disabled={disabled || busy}
        title="Download the witness list — accepted people only"
      >{busy ? 'Exporting…' : '⬇ Export ▾'}</button>
      {open && (
        <>
          <div style={S.backdrop} onClick={() => setOpen(false)} />
          <div style={S.menu}>
            {acceptedCount === 0 ? (
              <div style={S.note}>No accepted witnesses yet — accept people to include them in the list.</div>
            ) : FORMATS.map(([f, label]) => (
              <button key={f} type="button" style={S.menuItem} onClick={() => exportAs(f)} disabled={busy}>
                {label}
              </button>
            ))}
            {error && <div style={S.error}>{error}</div>}
          </div>
        </>
      )}
    </div>
  );
}

/**
 * WitnessExportCard — typed card for Arc's export_witness_list tool
 * (ChatMessage TOOL_CARDS): the same downloads, offered in the chat.
 */
export function WitnessExportCard({ result }) {
  const { caseId } = useParams();
  const { busy, error, exportAs } = useWitnessExport(caseId);
  if (!caseId || !result) return null;
  const count = result.accepted_count || 0;
  const formats = FORMATS.filter(([f]) => (result.formats || []).includes(f));
  return (
    <div style={S.card}>
      <span>
        <b>Witness list</b> — {count === 0
          ? 'no accepted witnesses yet. Accept people in the Witnesses tab to include them.'
          : `${count} accepted ${count === 1 ? 'person' : 'people'}`}
      </span>
      {count > 0 && (
        <span style={S.cardActions}>
          {formats.map(([f, label]) => (
            <button key={f} type="button" className="btn btn-sm" onClick={() => exportAs(f)} disabled={busy}>
              ⬇ {label}
            </button>
          ))}
        </span>
      )}
      {error && <span style={{ color: '#b91c1c' }}>{error}</span>}
    </div>
  );
}
